import React from "react";
import { useTranslation } from "react-i18next";
import { titleCase } from "title-case";
import { useFeatureEntitlements } from "@frontegg/react";
import { Box, Typography } from "@mui/material";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowRight01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Accordion } from "@outpave/ds";
import { Stack } from "components/layout/reusableLayouts";
import Summary, { SummaryItem } from "components/common/Summary";
import CardShippingSpeed from "enums/CardShippingSpeed";
import CardType from "enums/CardType";
import CustomFieldParentType from "enums/CustomFieldParentType";
import PolicyRequired from "enums/PolicyRequired";
import useCustomFieldRenderer from "hooks/useCustomFieldRenderer";
import { CardPolicy } from "utils/cards/cardFormUtils";

export interface CardSummaryProps {
  nickname: string;
  /** Set on an edit, which lists every field and marks what changed. */
  previousNickname?: string;
  cardholderName?: string;
  /** An edited card can be external; a new one is physical or virtual. */
  cardType?: string | null;
  /** Only when this card and this viewer have a shipping speed to show. */
  shippingSpeed?: CardShippingSpeed;
  policy: CardPolicy;
  previousPolicy?: CardPolicy;
}

type NamedList = Array<{ name?: string | null }> | null | undefined;

/** The old value struck through, then the new one, when they differ. */
const DiffText: React.FC<{ current: string; previous?: string }> = ({
  current,
  previous,
}) => {
  if (previous === undefined || previous === current) return <>{current}</>;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "var(--space-1)",
        flexWrap: "wrap",
      }}
    >
      <Typography
        component="span"
        variant="inherit"
        sx={{
          color: "var(--color-text-tertiary)",
          textDecoration: "line-through",
        }}
      >
        {previous}
      </Typography>
      <HugeiconsIcon
        icon={ArrowRight01Icon}
        size={12}
        color="var(--color-text-tertiary)"
      />
      <Typography
        component="span"
        variant="subtitle2"
        sx={{ color: "var(--color-text-brand)" }}
      >
        {current}
      </Typography>
    </Box>
  );
};

const names = (list: NamedList, merchants = false): string =>
  (list ?? [])
    .map((item) => item.name)
    .filter((name): name is string => !!name)
    // Merchant names arrive upper-cased from the card network.
    .map((name) => (merchants ? titleCase(name.toLowerCase()) : name))
    .join(", ");

const CardSummary: React.FC<CardSummaryProps> = ({
  nickname,
  previousNickname,
  cardholderName,
  cardType,
  shippingSpeed,
  policy,
  previousPolicy,
}) => {
  const { t } = useTranslation();
  const { isEntitled: featureMerchantLimits } = useFeatureEntitlements(
    "feature_merchant_limits",
  );
  const { isEntitled: featureAllocations } = useFeatureEntitlements(
    "feature_allocations",
  );
  const { isEntitled: featureCustomFields } = useFeatureEntitlements(
    "feature_card_custom_fields",
  );
  const { filteredCustomFields } = useCustomFieldRenderer({
    parentType: CustomFieldParentType.CARD,
    customFields: policy.customFields,
  });
  const { filteredCustomFields: previousCustomFields } = useCustomFieldRenderer(
    {
      parentType: CustomFieldParentType.CARD,
      customFields: previousPolicy?.customFields,
    },
  );

  const isEdit = !!previousPolicy;

  const formatMoney = (value: string): string =>
    !value ? t("No limit") : `$${Number(value).toLocaleString()}`;

  const formatPolicy = (requirement: PolicyRequired): string => {
    if (requirement === PolicyRequired.required) return t("Required");
    if (requirement === PolicyRequired.not_required) return t("Not Required");
    return t("Follow Policy");
  };

  const formatCancelDate = (value: CardPolicy["cancelationDate"]): string =>
    value
      ? new Date(value).toLocaleDateString(undefined, {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      : t("No cancelation date");

  const formatAllocation = (p: CardPolicy): string =>
    p.allocationType?.name
      ? `${p.allocationType.name} : ${p.allocationCode?.allocationCode ?? ""}`
      : t("No allocation");

  // A new card lists what is set; an edit lists every field, so a removed value still shows.
  const row = (
    label: string,
    getter: (p: CardPolicy) => string,
    isSet: (p: CardPolicy) => boolean,
    fullWidth = false,
  ): SummaryItem[] => {
    if (!isEdit && !isSet(policy)) return [];
    return [
      {
        title: t(label),
        value: (
          <DiffText
            current={getter(policy)}
            previous={previousPolicy ? getter(previousPolicy) : undefined}
          />
        ),
        fullWidth,
      },
    ];
  };

  const limit = (
    label: string,
    field:
      | "transactionLimit"
      | "dailyLimit"
      | "weeklyLimit"
      | "monthlyLimit"
      | "totalLimit",
  ) =>
    row(
      label,
      (p) => formatMoney(p[field]),
      (p) => !!p[field],
    );

  // Shown wherever a value exists, so a restriction is never hidden behind the entitlement.
  const restriction = (
    label: string,
    field:
      | "allowedMerchants"
      | "blockedMerchants"
      | "allowedCategories"
      | "blockedCategories",
    emptyLabel: string,
  ) => {
    const merchants =
      field === "allowedMerchants" || field === "blockedMerchants";
    const hasValue = !!names(policy[field]) || !!names(previousPolicy?.[field]);
    if (!featureMerchantLimits && !hasValue) return [];
    return row(
      label,
      (p) => names(p[field], merchants) || t(emptyLabel),
      (p) => !!names(p[field]),
      true,
    );
  };

  const detailItems: SummaryItem[] = [
    ...(cardholderName ? [{ title: t("User"), value: cardholderName }] : []),
    ...(isEdit
      ? [
          {
            title: t("Nickname"),
            value: (
              <DiffText
                current={nickname || t("No nickname")}
                previous={previousNickname || t("No nickname")}
              />
            ),
          },
        ]
      : nickname
        ? [{ title: t("Nickname"), value: nickname }]
        : []),
    {
      title: t("Type"),
      value:
        cardType === CardType.EXTERNAL
          ? t("External Card")
          : cardType === CardType.PHYSICAL
            ? t("Physical Card")
            : t("Virtual Card"),
    },
    ...(shippingSpeed
      ? [
          {
            title: t("Shipping Speed"),
            value:
              shippingSpeed === CardShippingSpeed.Express
                ? t("Express")
                : t("Standard"),
          },
        ]
      : []),
    ...row(
      "Cancelation Date",
      (p) => formatCancelDate(p.cancelationDate),
      (p) => !!p.cancelationDate,
    ),
  ];

  const limitItems: SummaryItem[] = [
    ...limit("Transaction Limit", "transactionLimit"),
    ...limit("Daily Limit", "dailyLimit"),
    ...limit("Weekly Limit", "weeklyLimit"),
    ...limit("Monthly Limit", "monthlyLimit"),
    ...limit("Total Card Limit", "totalLimit"),
    ...restriction(
      "Allowed Merchants",
      "allowedMerchants",
      "No merchant restriction",
    ),
    ...restriction(
      "Blocked Merchants",
      "blockedMerchants",
      "No blocked merchants",
    ),
    ...restriction(
      "Allowed Categories",
      "allowedCategories",
      "No category restriction",
    ),
    ...restriction(
      "Blocked Categories",
      "blockedCategories",
      "No blocked categories",
    ),
  ];

  const categorizationItems: SummaryItem[] = [
    ...row(
      "Expense Category",
      (p) => p.expenseCategory?.name || t("No category"),
      (p) => !!p.expenseCategory?.name,
    ),
    ...(featureAllocations
      ? row("Allocation", formatAllocation, (p) => !!p.allocationType?.name)
      : []),
  ];

  const policyItems: SummaryItem[] = [
    ...(featureAllocations
      ? row(
          "Allocation Policy",
          (p) => formatPolicy(p.allocationPolicy),
          () => true,
        )
      : []),
    ...row(
      "Receipt Policy",
      (p) => formatPolicy(p.receiptPolicy),
      () => true,
    ),
  ];

  const customFieldIds = Array.from(
    new Set(
      [...filteredCustomFields, ...(isEdit ? previousCustomFields : [])].map(
        (field) => field.id,
      ),
    ),
  );
  const customFieldItems: SummaryItem[] = customFieldIds.map((id) => {
    const current = filteredCustomFields.find((field) => field.id === id);
    const previous = previousCustomFields.find((field) => field.id === id);
    return {
      title: current?.label ?? previous?.label ?? "",
      value: (
        <DiffText
          current={current?.value || t("None")}
          previous={isEdit ? previous?.value || t("None") : undefined}
        />
      ),
    };
  });

  return (
    <Stack gap="0">
      <Box sx={{ marginBottom: "var(--space-3)" }}>
        <Summary disableContainer items={detailItems} />
      </Box>

      <Accordion title={t("Spending Limits")} defaultExpanded>
        {limitItems.length > 0 ? (
          <Summary disableContainer items={limitItems} />
        ) : (
          // The card an approver most needs to notice, so it is said plainly.
          <Typography variant="body1">
            {t("CardHasNoSpendingLimits")}
          </Typography>
        )}
      </Accordion>

      {categorizationItems.length > 0 && (
        <Accordion title={t("Categorizations")}>
          <Summary disableContainer items={categorizationItems} />
        </Accordion>
      )}

      <Accordion title={t("Policies")}>
        <Summary disableContainer items={policyItems} />
      </Accordion>

      {featureCustomFields && customFieldItems.length > 0 && (
        <Accordion title={t("Custom Fields")}>
          <Summary disableContainer items={customFieldItems} />
        </Accordion>
      )}
    </Stack>
  );
};

export default CardSummary;
